<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email, minLength } from "@vuelidate/validators";
import type { CmsElementForm } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { ApiClientError } from "@shopware/api-client";
import {
  useCmsElementConfig,
  useNavigationContext,
  useSalutations,
  useShopwareContext,
} from "#imports";
const { trackFormSubmit } = useSellograph();
import { computed, reactive, ref } from "vue";
import { defu } from "defu";

const props = defineProps<{
  content: CmsElementForm;
}>();

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Anrede",
    salutationPlaceholder: "Anrede wählen...",
    firstName: "Vorname",
    firstNamePlaceholder: "Vorname eingeben...",
    lastName: "Nachname",
    lastNamePlaceholder: "Nachname eingeben...",
    email: "E-Mail-Adresse",
    emailPlaceholder: "E-Mail eingeben...",
    phone: "Telefonnummer",
    phonePlaceholder: "Telefonnummer eingeben...",
    subject: "Thema",
    subjectPlaceholder: "Betreff eingeben...",
    comment: "Kommentar",
    commentPlaceholder: "Kommentar eingeben...",
    privacy: "Privatsphäre",
    dataProtection: "Ich habe die Datenschutzbestimmungen zur Kenntnis genommen.",
    submit: "Einreichen",
    messages: {
      contactFormSuccess:
        "Wir haben Ihre Kontaktanfrage erhalten und werden diese schnellstmöglich bearbeiten.",
    },
  },
};

translations = defu(translations, useCmsTranslations()) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const errorMessages = ref<any[]>([]);
const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();
const { getConfigValue } = useCmsElementConfig(props.content);

const getConfirmationText = computed(
  () =>
    getConfigValue("confirmationText") ??
    translations.form.messages.contactFormSuccess,
);
const getFormTitle = computed(() => getConfigValue("title") || "Contact");
const state = reactive({
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  comment: "",
  phone: "",
  checkbox: false,
});

const rules = computed(() => ({
  email: {
    required,
    email,
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  salutationId: {
    required,
  },
  phone: {
    required,
    minLength: minLength(3),
  },
  subject: {
    required,
    minLength: minLength(3),
  },
  comment: {
    required,
    minLength: minLength(10),
  },
  checkbox: {
    required,
    isTrue: (value: any) => value === true,
  },
}));

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    loading.value = true;
    try {
      await apiClient.invoke("sendContactMail post /contact-form", {
        ...state,
        navigationId: foreignKey.value,
      });
      formSent.value = true;
      trackFormSubmit({ formName: 'contact', email: state.email });
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      loading.value = false;
    }
  }
};
</script>
<template>
  <div class="section-contact-form">
    <form class="w-full relative" @submit.prevent="invokeSubmit">
      <h3 class="pb-3 mb-10 border-b border-gray-300">
        {{ getFormTitle }}
      </h3>

      <template v-if="!formSent">

        <div class="row">
          <div class="col-12 col-md-4 mb-5">
            <label for="salutation" class="mb-3">{{ translations.form.salutation }} *</label>
            <select
                id="salutation"
                name="salutation"
                class="form-control"
                :class="[
              $v.salutationId.$error
                ? 'border-danger'
                : 'border-gray',
            ]"
                v-model="state.salutationId"
                @blur="$v.salutationId.$touch()"
            >
              <option disabled selected value="">
                {{ translations.form.salutationPlaceholder }}
              </option>
              <option
                  v-for="salutation in getSalutations"
                  :key="salutation.id"
                  :value="salutation.id"
              >
                {{ salutation.displayName }}
              </option>
            </select>
            <span
                v-if="$v.salutationId.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.salutationId.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 col-md-4 mb-5">
            <label for="first-name" class="mb-3">{{ translations.form.firstName }} *</label>
            <input
                id="first-name"
                name="first-name"
                type="text"
                autocomplete="first-name"
                class="form-control"
                :class="[
              $v.firstName.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                @blur="$v.firstName.$touch()"
                v-model="state.firstName"
                :placeholder="translations.form.firstNamePlaceholder"
            />
            <span
                v-if="$v.firstName.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.firstName.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 col-md-4 mb-5">
            <label for="last-name" class="mb-3">{{ translations.form.lastName }} *</label>
            <input
                id="last-name"
                name="last-name"
                type="text"
                autocomplete="last-name"
                v-model="state.lastName"
                class="form-control"
                :class="[
              $v.lastName.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                @blur="$v.lastName.$touch()"
                :placeholder="translations.form.lastNamePlaceholder"
            />
            <span
                v-if="$v.lastName.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.lastName.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 col-md-4 mb-5">
            <label for="email-address" class="mb-3">{{ translations.form.email }} *</label>
            <input
                id="email-address"
                name="email"
                type="email"
                autocomplete="email"
                :class="[
              $v.email.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                v-model="state.email"
                @blur="$v.email.$touch()"
                class="form-control"
                :placeholder="translations.form.emailPlaceholder"
            />
            <span
                v-if="$v.email.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.email.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 col-md-4 mb-5">
            <label for="phone" class="mb-3">{{ translations.form.phone }} *</label>
            <input
                id="phone"
                name="phone"
                type="text"
                autocomplete="phone"
                v-model="state.phone"
                @blur="$v.phone.$touch()"
                class="form-control"
                :class="[
              $v.phone.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                :placeholder="translations.form.phonePlaceholder"
            />
            <span
                v-if="$v.phone.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.phone.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 col-md-4 mb-5">
            <label for="subject" class="mb-3">{{ translations.form.subject }} *</label>
            <input
                id="subject"
                name="subject"
                type="text"
                autocomplete="subject"
                v-model="state.subject"
                @blur="$v.subject.$touch()"
                class="form-control"
                :class="[
              $v.subject.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                :placeholder="translations.form.subjectPlaceholder"
            />
            <span
                v-if="$v.subject.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.subject.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 mb-5">
            <label for="comment" class="mb-3">{{ translations.form.comment }} *</label>
            <textarea
                id="comment"
                name="comment"
                type="text"
                autocomplete="comment"
                @blur="$v.comment.$touch()"
                v-model="state.comment"
                class="form-control"
                :class="[
              $v.comment.$error
                ? 'border-red-600 focus:border-red-600'
                : 'border-gray-300 focus:border-indigo-500',
            ]"
                :placeholder="translations.form.commentPlaceholder"
                rows="5"
            />
            <span
                v-if="$v.comment.$error"
                class="text-danger text-sm mt-2 d-inline-block"
            >
            {{ $v.comment.$errors[0].$message }}
          </span>
          </div>
          <div class="col-12 mb-5">
            <label for="privacy" class="mb-3">{{ translations.form.privacy }} *</label>
            <div class="d-flex">
              <input
                  id="privacy"
                  name="privacy"
                  type="checkbox"
                  v-model="state.checkbox"
                  class="me-3"
                  :class="[
                $v.checkbox.$error ? 'border-red-600' : 'border-gray-300',
              ]"
              />
              <div>
                <label
                    :class="[$v.checkbox.$error ? 'text-red-600' : '']"
                    for="privacy"
                >
                  {{ translations.form.dataProtection }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end mt-10">
          <button class="btn btn-primary" type="submit">
            {{ translations.form.submit }}
          </button>
        </div>
      </template>
      <template v-else>
        <p class="py-10 text-lg text-center">{{ getConfirmationText }}</p>
      </template>
    </form>
  </div>
</template>
